import 'swiper/css'
import 'swiper/css/effect-fade'
import 'swiper/css/pagination'

import { Trans } from '@lingui/macro'
import { useWeb3React } from '@web3-react/core'
import { MAX_WIDTH_MEDIA_BREAKPOINT, MEDIUM_MEDIA_BREAKPOINT } from 'components/Tokens/constants'
import { MouseoverTooltip } from 'components/Tooltip'
import usePoolAndPairQuery from 'graphql/competition/PoolAndPair'
import React from 'react'
import styled from 'styled-components/macro'
import { ThemedText } from 'theme'

import { SupportedChainId } from '../../constants/chains'
import useTopTraders from '../../graphql/competition/TopTrader'
import useUserInfo from '../../graphql/competition/UserInfo'
import useUserTrades from '../../graphql/competition/UserTrades'
import useDebounce from '../../hooks/useDebounce'
import Disclaimer from './Disclaimer'
import { useCompetitionDriver } from './Driver'
import { useRulesAccepted } from './hooks/useRulesAccepted'
import { ReferralAndRules } from './Modals/ReferralAndRules'
import PoolAndPairTable from './PoolAndPair'
import Tasks from './Tasks'
import TokenTable from './TokenTable/TokenTable'
import TopTraders from './TopTraders'

const ExploreContainer = styled.div`
  width: 100%;
  max-width: ${MAX_WIDTH_MEDIA_BREAKPOINT};
  min-width: 320px;
  padding: 12px;

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.md}px`}) {
    padding-top: 48px;
  }

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.sm}px`}) {
    padding-top: 20px;
  }
`
const TitleContainer = styled.div`
  max-width: ${MAX_WIDTH_MEDIA_BREAKPOINT};
  margin: 4rem auto 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  @media only screen and (max-width: ${MEDIUM_MEDIA_BREAKPOINT}) {
    flex-direction: column;
    align-items: stretch;
  }
`
const FiltersContainer = styled.div`
  display: flex;
  gap: 8px;
  height: 40px;

  @media only screen and (max-width: ${MEDIUM_MEDIA_BREAKPOINT}) {
    order: 2;
  }
`
const SearchContainer = styled(FiltersContainer)`
  margin: 0;
  width: auto;
  justify-content: flex-end;

  @media only screen and (max-width: ${MEDIUM_MEDIA_BREAKPOINT}) {
    margin: 0px;
    margin-top: 20px;
    order: 1;
  }
`

const TablesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const TradingCompetition = () => {
  const { chainId, account } = useWeb3React()
  const debouncedAccount = useDebounce(account, 500)
  const userTrades = useUserTrades(debouncedAccount)
  const userInfo = useUserInfo(debouncedAccount)
  const topTrades = useTopTraders()
  const rulesAccepted = useRulesAccepted(debouncedAccount, userInfo.data ? !!userInfo.data?.user : undefined)
  const { data: tokensData } = usePoolAndPairQuery()

  useCompetitionDriver(!!rulesAccepted.data)

  return (
    <>
      <ExploreContainer>
        {/*<Banner />*/}
        <Disclaimer />
        {debouncedAccount && userInfo.data?.user && chainId === SupportedChainId.XDC && <Tasks userInfo={userInfo} />}
        <ReferralAndRules userInfo={userInfo} rulesAccepted={rulesAccepted} />
        {debouncedAccount && chainId === SupportedChainId.XDC && (
          <>
            <TablesWrapper>
              <TokenTable
                userTrades={userTrades}
                userInfo={userInfo}
                topTrades={topTrades}
                account={account}
                tokensData={tokensData && tokensData.tokens ? tokensData.tokens : []}
              />
            </TablesWrapper>
          </>
        )}
        <TitleContainer>
          <MouseoverTooltip
            text={<Trans>This table lists the tokens and pools tracked for this competition.</Trans>}
            placement="bottom"
          >
            <ThemedText.LargeHeader>
              <Trans>Tracked Pools</Trans>
            </ThemedText.LargeHeader>
          </MouseoverTooltip>
        </TitleContainer>
        <TablesWrapper>
          <PoolAndPairTable />
        </TablesWrapper>
        <TitleContainer>
          <MouseoverTooltip
            text={
              <Trans>
                This table displays the top traders of the competition, organized by their respective scores.
              </Trans>
            }
            placement="bottom"
          >
            <ThemedText.LargeHeader>
              <Trans>Top Traders</Trans>
            </ThemedText.LargeHeader>
          </MouseoverTooltip>
        </TitleContainer>
        <TablesWrapper>
          <TopTraders topTrades={topTrades} />
        </TablesWrapper>
      </ExploreContainer>
    </>
  )
}

export default TradingCompetition
